import { FeatureFlag } from 'constants/feature-flags'
import { useApp } from 'context/app-context'
import { generateContext } from 'context/generate-context'
import { LocaleProvider } from 'context/locale-context'
import { useIsMounted } from 'hooks/use-is-mounted'
import Cookies from 'js-cookie'
import { PresentedTeamMember } from 'lib/presenters/team-member-presenter'
import { useRouter } from 'next/router'
import { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import { UnitShorthand } from 'types/unit-shorthand'

type CurrentUserContext = PresentedTeamMember & {
  hasFeature: (featureFlag: FeatureFlag) => boolean
  refreshUser: () => void
  isOwner: (ownerId: string) => boolean
  logout: () => void
  toggleDefaultUnit: () => void
  toggleProjectListView: () => void
  toggleZoomMode: (zoomCursorMode: boolean) => void
  getTeamMember: (id: number) => Partial<PresentedTeamMember> | undefined
  hideOnboarding: () => void
}

const defaultUser = {
  id: null,
  uuid: null,
  email: null,
  firstName: null,
  lastName: null,
  fullName: null,
  isAdmin: false,
  readOnly: false,
  isSuperadmin: false,
  createdAt: null,
  confirmedAt: null,
  features: null,
  team: null,
  teamId: 0,
  isTrial: false,
  isPromo: true,
  isCustomer: false,
  customerId: null,
  isExpired: false,
  daysUntilTrialEnds: 0,
  trialExpiredAt: null,
  defaultUnit: 'in' as UnitShorthand,
  minorDeviationLimit: 0.001,
  majorDeviationLimit: 0.005,
  defaultCutConfig: null,
  zoomCursorMode: true,
  freeOrbitMode: true,
  controls: 'Toolpath' as string,
  signsInWithPassword: true,
  projectListView: false,
  showOnboarding: false,
}

const [_CurrentUserProvider, useCurrentUser] =
  generateContext<CurrentUserContext>({
    ...defaultUser,
    hasFeature: () => false,
    refreshUser: () => null,
    isOwner: () => false,
    logout: () => null,
    toggleDefaultUnit: () => null,
    toggleProjectListView: () => null,
    toggleZoomMode: () => null,
    getTeamMember: () => null,
    hideOnboarding: () => null,
  })

type CurrentUserProviderProps = {
  chidren?: ReactNode
}

export const MockCurrentUserProvider: FC<
  CurrentUserProviderProps & { currentUser?: PresentedTeamMember }
> = ({ children, currentUser = defaultUser }) => {
  const [user, setUser] = useState<PresentedTeamMember>(currentUser)
  const isOwner = useCallback((ownerId) => ownerId === user.uuid, [user])

  const hasFeature = () => false
  const toggleDefaultUnit = () => null
  const toggleProjectListView = () => null

  const refreshUser = () => {
    setUser(defaultUser)
    setUser(currentUser)
  }

  const logout = () => setUser(null)

  const toggleZoomMode = () => null

  const getTeamMember = () => null

  const hideOnboarding = () => null

  return (
    <_CurrentUserProvider
      value={{
        ...currentUser,
        hasFeature,
        refreshUser,
        isOwner,
        logout,
        toggleDefaultUnit,
        toggleProjectListView,
        toggleZoomMode,
        getTeamMember,
        hideOnboarding,
      }}>
      {children}
    </_CurrentUserProvider>
  )
}

export const CurrentUserProvider: FC<CurrentUserProviderProps> = ({
  children,
}) => {
  const router = useRouter()
  const { env } = useApp()
  const [user, setUser] = useState<PresentedTeamMember>(defaultUser)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const isMounted = useIsMounted()
  const isOwner = useCallback((ownerId) => ownerId === user.uuid, [user])

  useEffect(() => {
    if (!user) {
      return
    }

    setShowOnboarding(
      user.showOnboarding && !localStorage.getItem('onboarding-hidden'),
    )
  }, [user])

  const hideOnboarding = useCallback(() => {
    // No need to await this fetch.
    fetch('/api/teams', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        csrf: Cookies.get('csrf'),
        showOnboarding: false,
      }),
    })
    localStorage.setItem(
      'onboarding-hidden',
      Math.round(Date.now() / 1000).toString(),
    )
    setShowOnboarding(false)
  }, [user])

  const hasFeature = (featureFlag: FeatureFlag): boolean => {
    return user.features?.includes(featureFlag)
  }

  const redirectToLogin = () => {
    // hard refresh to clear any swr cache
    if (router.pathname !== '/login') {
      window.location.href = '/login'
    }
  }

  const refreshUser = () => {
    setUser(defaultUser)
    getUser()
  }

  const logout = async () => {
    await fetch('/api/users/logout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ csrf: Cookies.get('csrf') }),
    })
    redirectToLogin()
  }

  const getUser = useCallback(async () => {
    try {
      const response = await fetch(`${env.BASE_URL}/api/users/current`, {
        method: 'GET',
      })

      const { data } = (await response.json()) as {
        data: { user: PresentedTeamMember }
      }
      if (isMounted()) {
        setUser(data.user)
      }
    } catch (e) {}
  }, [])

  const toggleProjectListView = async () => {
    if (!user) {
      return
    }

    // Optimistic update
    setUser({ ...user, projectListView: !user.projectListView })

    fetch(`${env.BASE_URL}/api/users/${user.uuid}/project-list-view`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        csrf: Cookies.get('csrf'),
        data: { projectListView: !user.projectListView },
      }),
    })
  }

  const toggleDefaultUnit = async () => {
    if (!user) {
      return
    }

    const newUnit = user.defaultUnit === 'in' ? 'mm' : 'in'

    // Optimistic update
    setUser({
      ...user,
      defaultUnit: newUnit,
    })

    fetch(`${env.BASE_URL}/api/users/${user.uuid}/units`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        csrf: Cookies.get('csrf'),
        data: {
          defaultUnit: newUnit,
        },
      }),
    })
  }

  const toggleZoomMode = async (zoomCursorMode: boolean) => {
    if (!user) {
      return
    }

    setUser((prevUser) => {
      return { ...prevUser, zoomCursorMode }
    })

    fetch(`${env.BASE_URL}/api/users/${user.uuid}/zoom-mode`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        csrf: Cookies.get('csrf'),
        data: {
          zoomCursorMode,
        },
      }),
    })
  }

  useEffect(() => {
    getUser()
  }, [])

  const getTeamMember = useCallback(
    (id: number): Partial<PresentedTeamMember> => {
      if (!id) {
        return {
          id: 0,
          uuid: '',
          email: '',
          firstName: 'Toolpath',
          lastName: '',
          fullName: 'Toolpath',
          confirmedAt: Date(),
        } as Partial<PresentedTeamMember>
      }

      const unknownUser = {
        id: -1,
        uuid: '',
        email: '',
        firstName: 'Unknown',
        lastName: 'User',
        fullName: 'Unknown User',
        confirmedAt: Date(),
      } as Partial<PresentedTeamMember>

      if (!user?.teamMembers || !user?.teamMembers?.length) {
        return unknownUser
      }

      const foundUser = user.teamMembers.find((member) => member.id === id)
      return foundUser || unknownUser
    },
    [user],
  )

  return (
    <_CurrentUserProvider
      value={{
        ...user,
        showOnboarding,
        hideOnboarding,
        hasFeature,
        refreshUser,
        isOwner,
        logout,
        toggleDefaultUnit,
        toggleProjectListView,
        toggleZoomMode,
        getTeamMember,
      }}>
      <LocaleProvider>{children}</LocaleProvider>
    </_CurrentUserProvider>
  )
}

export { useCurrentUser }
